import Link from 'next/link'

import { Poster } from '@/app/components/atoms/Poster'
import { Tag } from '@/app/components/atoms/Tag'
import { cx } from '@/app/lib/utils'

import styles from './RankingCard.module.scss'

interface RankingCardProps {
  readonly title: string
  readonly href: string
  readonly img: string
  readonly date: string
  readonly venue: string
  /** Posición en el ranking (1-10) — badge cuadrado sobre la imagen (FR-017). */
  readonly rank: number
  /** Estado de compra no comprable (Cancelado/Agotado/No disponible/Disponible
   * el <fecha>/texto CMS) — mismo campo que el resto del catálogo
   * (`mapProductToCard`/`resolvePurchaseState`, specs/019-estados-compra-eventos).
   * Ausente ⇒ estado por defecto, no se pinta nada encima de la imagen. */
  readonly imageBadge?: string
  readonly className?: string
}

/**
 * `RankingCard` — tarjeta propia del módulo CMS «ProductosLocalizados»:
 * imagen a sangre (`Poster`) con degradado inferior SIEMPRE visible que aloja
 * el badge de puesto (cuadrado, acento verde) junto a ciudad + fechas — Figma
 * "Productos Localizados" (nodo 5958:48479). Redisño frente a la versión
 * anterior (número gigante que sangraba por el borde + fecha/título/venue/
 * precio apilados): el nuevo diseño NO muestra precio ni el título del evento
 * como texto — el nombre del espectáculo ya lo transmite el propio artwork
 * del póster; solo quedan ciudad y fechas junto al badge.
 *
 * Toda la tarjeta es un único enlace hacia `href`; el overlay visual se marca
 * `aria-hidden` — el nombre accesible del enlace incluye puesto + título +
 * venue (aunque el título no se pinte visualmente, sigue identificando el
 * evento para lectores de pantalla), mismo criterio que la referencia
 * `eci-firefly-uikit/ProductosLocalizados/RankingCard.tsx`.
 *
 * Estado de compra no comprable (`imageBadge`): igual que el resto del
 * catálogo (`VerticalCard`/`ProductPosterCard`), pinta una tag roja CENTRADA
 * sobre toda la imagen y la pasa a blanco y negro (`.imageWrapMuted`) —
 * mismo criterio visual, aplicado aquí sobre el badge de puesto/ciudad/fecha
 * que ya tiene esta card (specs/019-estados-compra-eventos).
 *
 * Server Component: presentación pura, sin estado ni efectos.
 */
export function RankingCard({
  title,
  href,
  img,
  date,
  venue,
  rank,
  imageBadge,
  className,
}: RankingCardProps) {
  const accessibleLabel = `Puesto ${rank}: ${[title, venue].filter(Boolean).join(' — ')}`

  return (
    <Link href={href} prefetch={false} className={cx(styles.card, className)} aria-label={accessibleLabel}>
      <div className={cx(styles.imageWrap, imageBadge && styles.imageWrapMuted)}>
        {img && <Poster src={img} alt="" className={styles.poster} />}

        {imageBadge && (
          <div className={styles.imageBadge}>
            <Tag variant="status">{imageBadge}</Tag>
          </div>
        )}
      </div>

      <div className={styles.overlay} aria-hidden="true">
        <div className={styles.row}>
          <span className={styles.rankBadge}>{rank}</span>
          <div className={styles.info}>
            {venue && <p className={styles.venue}>{venue}</p>}
            {date && <p className={styles.date}>{date}</p>}
          </div>
        </div>
      </div>
    </Link>
  )
}
